import React, { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@illegally-parked/routes/Routes';
import { SelectInput, SelectOption } from '@organisms/*';
import { issueTypes } from '../../../../constants/issueTypes';
import {
  FormTitle,
  NavigationButtons,
  InformationBox,
  TextArea,
} from '@molecules/*';
import { useParkingStore } from '@illegally-parked/store/store';
import { stringFieldSchema, parseZodErrors } from '../../validations/issueType';
import { ErrorMessageProps, TextAreaFlexibleLengthSchema } from '@utils';
import { ErrorSummary } from '@atoms/*';

const otherInfoBoxTitle = 'This vehicle might not be illegally parked';
const otherInfoBoxContent =
  '<p>We can only take action if our officers find clear evidence it is an illegally parked vehicle and it matches one of the options on the form.</p>';

const otherInputLabel = 'Please provide details of the issue';
const otherInputDescription =
  'For example, what kind of restricted zone is the vehicle parked or in what way is it blocking the road?';

export const IssueType: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    updateIssueType,
    issueType,
    otherIssueDetails,
    updateOtherIssueDetails,
  } = useParkingStore();

  const [errorMessages, setErrorMessages] = useState<ErrorMessageProps[]>([]);
  const [isShowResidentBayInfo, setIsShowResidentBayInfo] =
    useState<Boolean>(false);
  const MAX_LENGTH = 500;

  const issueTypeLabel =
    issueTypes.find(({ value }) => value === issueType)?.label || '';

  const handleTypeOfIssue = (option: SelectOption) => {
    const type = option.value;
    setErrorMessages([]);
    updateIssueType(type);

    // to hide/show info box for ResidentBay type
    if (type === 'ResidentBay') {
      setIsShowResidentBayInfo(true);
    } else {
      setIsShowResidentBayInfo(false);
    }
  };

  const handlePrevious = () => {
    navigate(RoutesList.PARKING_HOME);
  };

  const handleNext = () => {
    try {
      const issueTypeSchema = stringFieldSchema(
        'issueType',
        'Select the type of issue',
      );
      issueTypeSchema.parse({ issueType });

      if (issueType === 'Other') {
        TextAreaFlexibleLengthSchema({
          fieldNames: ['otherDetails'],
          maxLength: MAX_LENGTH,
        }).parse({
          otherDetails: otherIssueDetails,
        });
      }

      navigate(RoutesList.VEHICLE_DETAILS);
    } catch (error: any) {
      const errors = parseZodErrors(error);
      setErrorMessages(errors);
    }
  };

  const handleChangeOtherDetails = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    updateOtherIssueDetails(event.target.value);
    setErrorMessages([]);
  };

  const residentBayInfoBoxTitle = 'It is no longer required to display permits';
  const residentBayInfoBoxContent =
    "<p>This vehicle might belong to someone new to the area. Enter the vehicle's registration number in the next step so our officers can check if it has a parking permit.</p>";

  return (
    <>
      <ErrorSummary messages={errorMessages} />
      <form className="flex flex-col">
        <section data-testid="issue-type-heading">
          <FormTitle text="What is the type of issue?" />
        </section>
        <SelectInput
          id="type of issue"
          options={issueTypes}
          className="mb-5 normal-case"
          title="Select the issue type"
          placeholderText={issueType.length > 0 ? issueTypeLabel : ''}
          onChange={(option: SelectOption) => handleTypeOfIssue(option)}
          errorPath="issueType"
          errorMessages={errorMessages}
        />

        {isShowResidentBayInfo || issueType === 'ResidentBay' ? (
          <InformationBox
            title={residentBayInfoBoxTitle}
            infoText={residentBayInfoBoxContent}
            variant="info"
            data-testid="resident-bay-info-box"
          />
        ) : null}

        {issueType === 'Other' ? (
          <div>
            <InformationBox
              title={otherInfoBoxTitle}
              infoText={otherInfoBoxContent}
              variant="warning"
              data-testid="other-info-box"
            />
            <TextArea
              labelText={otherInputLabel}
              isLabelTextTitle={false}
              description={otherInputDescription}
              value={otherIssueDetails}
              onChange={handleChangeOtherDetails}
              errorPath="otherDetails"
              errorMessages={errorMessages}
              showCounter={true}
            />
          </div>
        ) : null}

        <NavigationButtons
          buttonNext={{
            onClick: handleNext,
          }}
          buttonPrevious={{
            onClick: handlePrevious,
          }}
        />
      </form>
    </>
  );
};
